import React, { useState } from "react";
import { StyledStatusHeader, StyledStatusRow } from "./StatusHeader.styled";
import {
  CmSheetEventEnum,
  CmSheetStateEnum,
  cmSheetStatesOrdered,
  cmSheetStatusLabels,
  PermissionsEnum,
} from "../../../../../constants/AppConstants";
import { BodyTyp } from "../../../../../components/Typography/Typography";
import { FontWeightEnum, IconSizeEnum } from "../../../../../types/AppTypes";
import { theme } from "../../../../../constants/StyledComponents";
import { ChangeStatusModal } from "./ChangeStatusModal/ChangeStatusModal";
import { roleHasPermission } from "../../../../../utils/roleAuth.utils";
import { useAppSelector } from "../../../../../hooks/store.hook";
import {
  Button,
  ButtonSizes,
} from "../../../../../components/core/Button/Button";
import {
  CM_SHEET_SENT_LABEL,
  COLLABORATOR_COMMENT_LABEL,
  DEVELOPMENT_TITLE_LABEL,
  EDIT_STATUS_LABEL,
  EDIT_STATUS_TOOLTIP_MESSAGE,
  EMPTY_STRING,
  FINAL_INTERVIEW_LABEL,
  GOALS_TOOLTIP_2_LABEL,
  GOALS_TOOLTIP_LABEL,
  INTERNAL_GRADE_LABEL,
  PROFESSIONAL_PATH_LABEL,
  RESTORE_CM_SHEET_LABEL,
  RESTORE_LABEL,
  RESTORE_MESSAGE,
  SOFT_SKILLS_LABEL,
} from "../../../../../constants/Dictionary";
import { ReactComponent as EditIcon } from "../../../../../icons/editBox.svg";
import { ReactComponent as CheckIcon } from "../../../../../icons/check.svg";
import { ReactComponent as RestoreIcon } from "../../../../../icons/restore.svg";
import {
  CmSheetStateMachine,
  CollaboratorCmSheet,
} from "../../../../../types/api/collaborators.type";
import { IconManager } from "../../../../../components/core/IconManager/IconManager";
import { PopupConfirm } from "../../../../../components/PopupConfirm/PopupConfirm";
import { findStatusLabelByValue } from "../../../../../utils/orgFunctions.utils";
import {
  currentStateIsLessEqualThanTargetState,
  currentStateIsMoreThanTargetState,
} from "../../../../../utils/cmSheet.utils";
import { useEditing } from "../../../../../providers/editing.provider";
import { useUpdateCmSheetStateMachine } from "../../../../../hooks/cmSheet/stateMachine.hook";
import { Tooltip } from "../../../../../components/core/Tooltip/Tooltip";
import { TooltipPositionEnum } from "../../../../../components/core/Tooltip/Tooltip.style";
import {
  CmSectionsTooltip,
  CmTooltipSection,
} from "../Components/CmSectionsTooltip/CmSectionsTooltip";

type StatusProps = {
  cmSheetStateMachineInfo: CmSheetStateMachine;
  cmSheetInfo: CollaboratorCmSheet;
  isInterim: boolean;
  isMyProfile?: boolean;
};

export const StatusHeader = ({
  cmSheetStateMachineInfo,
  cmSheetInfo,
  isInterim,
  isMyProfile,
}: StatusProps) => {
  const { orgSettings } = useAppSelector((state) => state.config);
  const { profile } = useAppSelector((state) => state.user);
  const { edit } = useEditing();

  const [openStatusModal, setOpenStatusModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const { updateCmSheetStateMachine } = useUpdateCmSheetStateMachine(
    cmSheetInfo.id,
  );

  const isRestoreVisible = () => {
    return (
      !isMyProfile &&
      cmSheetStateMachineInfo.currentState ===
        CmSheetStateEnum.ARCHIVED_SHEET_WITH_TERMINATION &&
      roleHasPermission(profile, PermissionsEnum.RESTORE_CM_SHEET)
    );
  };

  const isChangeStatusVisible = () => {
    if (!isMyProfile) {
      if (
        roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS) &&
        cmSheetStateMachineInfo.currentState !==
          CmSheetStateEnum.ARCHIVED_SHEET_WITH_TERMINATION
      ) {
        return true;
      }

      if (
        roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET) &&
        profile.id === cmSheetInfo.careerManagerId &&
        [
          CmSheetStateEnum.DRAFT,
          CmSheetStateEnum.INTERMEDIATE_EVALUATION,
        ].includes(cmSheetStateMachineInfo.currentState)
      ) {
        return true;
      }
    }

    return false;
  };

  const onConfirmRestore = async () => {
    await updateCmSheetStateMachine({
      event: CmSheetEventEnum.RESTORE,
    });
    setShowRestoreModal(false);
  };

  const getStatusList = () => {
    if (
      cmSheetStateMachineInfo.currentState ===
      CmSheetStateEnum.ARCHIVED_SHEET_WITH_TERMINATION
    ) {
      let list: CmSheetStateEnum[] = [];
      cmSheetStatesOrdered.forEach((item) => {
        if (
          item === CmSheetStateEnum.ARCHIVED_SHEET_WITH_TERMINATION ||
          (cmSheetStateMachineInfo.previousState !== null &&
            currentStateIsLessEqualThanTargetState(
              item,
              cmSheetStateMachineInfo.previousState,
            ))
        ) {
          list.push(item);
        }
      });
      return list;
    } else {
      return cmSheetStatesOrdered.filter(
        (item) => item !== CmSheetStateEnum.ARCHIVED_SHEET_WITH_TERMINATION,
      );
    }
  };

  const getHeaderTooltipSection = (isInterim: boolean): CmTooltipSection[] => [
    {
      index: 1,
      label: INTERNAL_GRADE_LABEL,
      isComplete:
        cmSheetStateMachineInfo.changeCMSheetStatusValidationSection
          .internalGrade ?? false,
    },
    {
      index: 2,
      label: SOFT_SKILLS_LABEL,
      isComplete:
        cmSheetStateMachineInfo.changeCMSheetStatusValidationSection
          .softSkills ?? false,
    },
    {
      index: 3,
      label: PROFESSIONAL_PATH_LABEL,
      isComplete:
        cmSheetStateMachineInfo.changeCMSheetStatusValidationSection
          .careerPath ?? false,
    },
    {
      index: 4,
      label:
        cmSheetStateMachineInfo.currentState === CmSheetStateEnum.DRAFT
          ? GOALS_TOOLTIP_LABEL
          : GOALS_TOOLTIP_2_LABEL,
      isComplete:
        cmSheetStateMachineInfo.changeCMSheetStatusValidationSection.goals ??
        false,
    },
    ...([
      CmSheetStateEnum.INTERMEDIATE_EVALUATION,
      CmSheetStateEnum.FINAL_CALIBRATION,
      CmSheetStateEnum.FINAL_FEEDBACK,
    ].includes(cmSheetStateMachineInfo.currentState)
      ? [
          {
            index: 5,
            label: DEVELOPMENT_TITLE_LABEL,
            isComplete:
              cmSheetStateMachineInfo.changeCMSheetStatusValidationSection
                .development ?? false,
          },
          {
            index: 7,
            label: COLLABORATOR_COMMENT_LABEL,
            isComplete:
              cmSheetStateMachineInfo.changeCMSheetStatusValidationSection
                .collaboratorFeedback ?? false,
          },
          {
            index: 8,
            label: FINAL_INTERVIEW_LABEL,
            isComplete:
              cmSheetStateMachineInfo.changeCMSheetStatusValidationSection
                .finalInterview ?? false,
          },
        ]
      : []),
    ...([
      CmSheetStateEnum.DRAFT,
      CmSheetStateEnum.INTERMEDIATE_EVALUATION,
      CmSheetStateEnum.FINAL_FEEDBACK,
    ].includes(cmSheetStateMachineInfo.currentState) && !isInterim
      ? [
          {
            label: CM_SHEET_SENT_LABEL,
            isComplete: cmSheetStateMachineInfo.isRevisionRequestSent,
          },
        ]
      : []),
  ];

  const tooltipSections = getHeaderTooltipSection(isInterim);

  return (
    <>
      <StyledStatusHeader>
        {
          <>
            {isRestoreVisible() ? (
              <Button
                label={RESTORE_LABEL}
                onClick={() => setShowRestoreModal(true)}
                size={ButtonSizes.SMALL}
                icon={<RestoreIcon />}
              />
            ) : null}
            {isChangeStatusVisible() ? (
              <Tooltip
                position={TooltipPositionEnum.BOTTOM}
                message={
                  <CmSectionsTooltip
                    message={EDIT_STATUS_TOOLTIP_MESSAGE}
                    sections={tooltipSections}
                  />
                }
                hide={
                  cmSheetStateMachineInfo.currentState ===
                  CmSheetStateEnum.ARCHIVED_SHEET
                }
              >
                <Button
                  label={EDIT_STATUS_LABEL}
                  icon={<EditIcon />}
                  onClick={() => setOpenStatusModal(true)}
                  size={ButtonSizes.SMALL}
                  disabled={edit}
                />
              </Tooltip>
            ) : null}
            <StyledStatusRow>
              <div className="steps-container">
                {getStatusList().map((item, index) => {
                  const isActive =
                    item === cmSheetStateMachineInfo.currentState;
                  const isComplete =
                    currentStateIsMoreThanTargetState(
                      cmSheetStateMachineInfo.currentState,
                      item,
                    ) ||
                    (cmSheetStateMachineInfo.currentState === item &&
                      index === getStatusList().length - 1);
                  return (
                    <div key={index + 1}>
                      <span
                        className={`${isActive && "active"} ${isComplete && "complete"}`}
                      >
                        {isComplete ? (
                          <IconManager
                            size={IconSizeEnum.SMALL}
                            color={theme.colors.white}
                          >
                            <CheckIcon />
                          </IconManager>
                        ) : (
                          <BodyTyp
                            fontWeight={FontWeightEnum.MEDIUM}
                            color={
                              isActive
                                ? theme.colors.purple
                                : theme.colors.black
                            }
                          >
                            {index + 1}
                          </BodyTyp>
                        )}
                      </span>
                      <BodyTyp
                        fontWeight={FontWeightEnum.MEDIUM}
                        color={
                          isComplete ? theme.colors.purple : theme.colors.black
                        }
                      >
                        {cmSheetStatusLabels[item]}
                      </BodyTyp>
                      <div className="step-divider" />
                    </div>
                  );
                })}
              </div>
            </StyledStatusRow>
          </>
        }
      </StyledStatusHeader>
      <ChangeStatusModal
        isOpen={openStatusModal}
        cmSheetStateMachine={cmSheetStateMachineInfo}
        cmSheetInfo={cmSheetInfo}
        closeModal={() => setOpenStatusModal(false)}
        isInterim={isInterim}
      />
      <PopupConfirm
        isOpen={showRestoreModal}
        label={RESTORE_CM_SHEET_LABEL}
        popupText={`${RESTORE_MESSAGE} "${findStatusLabelByValue(orgSettings.cmsheet, cmSheetStateMachineInfo.previousState) ?? EMPTY_STRING}"?`}
        onConfirm={onConfirmRestore}
        onCancel={() => setShowRestoreModal(false)}
      />
    </>
  );
};
